import { useCallback, useState } from "react";
import { useChatContext } from "stream-chat-react";

import { useAuth } from "@/features/auth/context/AuthContext";
import { getColumns, createColumn } from "@/features/tasks/services/taskColumnService";
import type { TaskColumn } from "@/features/tasks/types/task";

const CHAT_TASK_COLUMN_LABEL = "Chat Task";

export type CreateTaskSource = {
  messageId: string;
  channelId: string;
  text: string;
  channelMembers: Array<{ user_id: string; first_name: string; last_name: string; profile_pic_id?: string }>;
};

// "Create Task" from a chat message: finds or creates the "Chat Task" column
// and pre-fills the task form with the message + channel members (minus self).
export const useCreateTaskFromMessage = () => {
  const { channel } = useChatContext();
  const { userData } = useAuth();

  const [createTaskSource, setCreateTaskSource] = useState<CreateTaskSource | null>(null);
  const [taskColumns, setTaskColumns] = useState<TaskColumn[]>([]);
  const [taskDefaultColumnId, setTaskDefaultColumnId] = useState<string>("");

  const handleCreateTaskFromMessage = useCallback(
    async (message: any, channelMembers: any[]) => {
      if (!message?.text) return;
      try {
        let cols = await getColumns("user");
        // Find or create the "Chat Task" column
        let chatTaskCol = cols.find(
          (c) => c.label.toLowerCase() === CHAT_TASK_COLUMN_LABEL.toLowerCase(),
        );
        if (!chatTaskCol) {
          chatTaskCol = await createColumn(
            { label: CHAT_TASK_COLUMN_LABEL, color: "blue", col_order: cols.length },
            "user",
          );
          cols = [...cols, chatTaskCol];
        }
        setTaskColumns(cols);
        setTaskDefaultColumnId(chatTaskCol.id);
      } catch {
        setTaskColumns([]);
        setTaskDefaultColumnId("");
      }
      // Exclude self from pre-selected members
      const selfId = String(userData?.id ?? "");
      const others = channelMembers.filter((m) => m.user_id !== selfId);
      setCreateTaskSource({
        messageId: message.id,
        channelId: channel?.id ?? "",
        text: message.text,
        channelMembers: others,
      });
    },
    [channel, userData?.id],
  );

  return {
    createTaskSource,
    setCreateTaskSource,
    taskColumns,
    taskDefaultColumnId,
    handleCreateTaskFromMessage,
  };
};
